import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { _registerToastHandler, _unregisterToastHandler } from "@/lib/toast";
import type { ToastItem } from "@/lib/toast";
import { ToastContainer } from "@/components/ui/Toast";

// ─── Context ─────────────────────────────────────────────────────────────────

interface ToastContextValue {
  toasts: ToastItem[];
  addToast: (item: ToastItem) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

// ─── Provider ────────────────────────────────────────────────────────────────

/**
 * Provides the toast system to the app tree.
 *
 * Wrap your root layout with this component:
 *
 *   <ToastProvider>
 *     <App />
 *   </ToastProvider>
 *
 * Once mounted, imperative calls from `@/lib/toast` will enqueue toasts and
 * `ToastContainer` will render them overlaid at the top of the screen.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const addToast = useCallback((item: ToastItem) => {
    setToasts((prev) => [...prev, item]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Register the imperative handler so toast.success/error/info() works.
  // Using a stable ref so the effect never re-runs unnecessarily.
  const addToastRef = useRef(addToast);
  addToastRef.current = addToast;

  useEffect(() => {
    _registerToastHandler((item) => addToastRef.current(item));
    return () => {
      _unregisterToastHandler();
    };
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      {/* ToastContainer renders absolutely-positioned toasts on top of the tree */}
      <ToastContainer toasts={toasts} onDismiss={removeToast} />
    </ToastContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Access the toast context imperatively from inside the React tree.
 *
 * Prefer using the `toast` singleton from `@/lib/toast` for most cases.
 * Use this hook when you need direct access to `removeToast` or `toasts`.
 *
 * @example
 * const { addToast, removeToast } = useToastContext();
 */
export function useToastContext(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToastContext must be used within a <ToastProvider>");
  }
  return ctx;
}
